import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { analytics } from '../services/analytics';
import SEOPageLayout from '../components/SEOPageLayout';

// Anchors drawn from the ServiceTitan HVAC close-rate survey reported in
// ACHR News — "offering 4+ options" lifts close rate by ~10 percentage
// points and the combined effect on ticket size is ~+45% (e.g. $4,500 → $6,500+
// on HVAC installs). The calculator applies these as industry-agnostic
// multipliers so it's useful across trades, not only HVAC.
const CLOSE_RATE_LIFT_PP = 10;
const TICKET_LIFT_MULTIPLIER = 1.45;

const formatUsd = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.max(0, value));

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

const RoiCalculator = () => {
  const [quotesPerMonth, setQuotesPerMonth] = useState(15);
  const [closeRate, setCloseRate] = useState(35);
  const [avgTicket, setAvgTicket] = useState(5000);

  useEffect(() => {
    analytics.track('ROI Calculator Viewed');
  }, []);

  const math = useMemo(() => {
    const quotes = Number(quotesPerMonth) || 0;
    const closePct = clamp(Number(closeRate) || 0, 0, 100);
    const ticket = Number(avgTicket) || 0;

    const newClosePct = clamp(closePct + CLOSE_RATE_LIFT_PP, 0, 100);
    const newTicket = ticket * TICKET_LIFT_MULTIPLIER;

    const currentWins = quotes * (closePct / 100);
    const newWins = quotes * (newClosePct / 100);

    const currentRevenue = currentWins * ticket;
    const newRevenue = newWins * newTicket;

    const extraMonthly = newRevenue - currentRevenue;
    const extraYearly = extraMonthly * 12;

    return {
      closePct,
      newClosePct,
      ticket,
      newTicket,
      currentWins,
      newWins,
      currentRevenue,
      newRevenue,
      extraMonthly,
      extraYearly,
    };
  }, [quotesPerMonth, closeRate, avgTicket]);

  return (
    <SEOPageLayout
      title="Tiered Quoting ROI Calculator — See Your Revenue Lift | QuoteTier"
      description="Calculate how much more revenue you'd make with Good/Better/Best quoting. Uses ServiceTitan close-rate survey data: +10pp close rate, +45% average ticket."
      canonical="/roi"
      ogTitle="How Much Revenue Tiered Quoting Would Add to Your Business"
      ogDescription="Enter your quotes/month, close rate, and average ticket. See the dollar impact of Good/Better/Best pricing — backed by ServiceTitan industry data."
    >
      <section className="py-24 md:py-32 lg:py-40">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              See the <span className="text-primary font-bold">math</span> on tiered quoting.
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed">
              Three numbers from your business. One number back: the revenue you're leaving on the table by sending flat-rate quotes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-0 border border-black rounded-2xl overflow-hidden">
            {/* Inputs */}
            <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-black">
              <h2 className="font-instrument text-2xl md:text-3xl font-bold mb-6">Your numbers</h2>
              <div className="space-y-6">
                <label className="block">
                  <span className="font-jetbrains text-sm text-gray-600 mb-2 block">Quotes sent per month</span>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={quotesPerMonth}
                    onChange={(e) => setQuotesPerMonth(e.target.value)}
                    className="w-full px-4 py-3 border border-black rounded-lg font-instrument text-2xl focus:outline-none focus:ring-2 focus:ring-black"
                  />
                </label>
                <label className="block">
                  <span className="font-jetbrains text-sm text-gray-600 mb-2 block">Current close rate (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={closeRate}
                    onChange={(e) => setCloseRate(e.target.value)}
                    className="w-full px-4 py-3 border border-black rounded-lg font-instrument text-2xl focus:outline-none focus:ring-2 focus:ring-black"
                  />
                </label>
                <label className="block">
                  <span className="font-jetbrains text-sm text-gray-600 mb-2 block">Average won ticket ($)</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={avgTicket}
                    onChange={(e) => setAvgTicket(e.target.value)}
                    className="w-full px-4 py-3 border border-black rounded-lg font-instrument text-2xl focus:outline-none focus:ring-2 focus:ring-black"
                  />
                </label>
              </div>
              <p className="font-jetbrains text-xs text-gray-500 mt-6 leading-relaxed">
                Not sure? Typical HVAC install contractors: 15 quotes/mo · 35% close · $5,000 ticket. Painters: 20 · 40% · $1,800. Roofers: 12 · 30% · $9,000.
              </p>
            </div>

            {/* Results */}
            <div className="p-8 md:p-10 bg-black text-white">
              <h2 className="font-instrument text-2xl md:text-3xl font-bold mb-6">With tiered quoting</h2>
              <div className="space-y-5">
                <div className="flex items-baseline justify-between">
                  <span className="font-jetbrains text-sm text-gray-400">Close rate</span>
                  <span className="font-instrument text-xl">
                    <span className="text-gray-500 line-through mr-2">{math.closePct}%</span>
                    <span className="font-bold">{math.newClosePct}%</span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="font-jetbrains text-sm text-gray-400">Average ticket</span>
                  <span className="font-instrument text-xl">
                    <span className="text-gray-500 line-through mr-2">{formatUsd(math.ticket)}</span>
                    <span className="font-bold">{formatUsd(math.newTicket)}</span>
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="font-jetbrains text-sm text-gray-400">Monthly revenue</span>
                  <span className="font-instrument text-xl">
                    <span className="text-gray-500 line-through mr-2">{formatUsd(math.currentRevenue)}</span>
                    <span className="font-bold">{formatUsd(math.newRevenue)}</span>
                  </span>
                </div>
                <div className="pt-6 mt-6 border-t border-gray-700">
                  <p className="font-jetbrains text-sm text-gray-400 mb-2">Extra revenue per month</p>
                  <p className="font-instrument text-5xl md:text-6xl font-bold text-primary">
                    {formatUsd(math.extraMonthly)}
                  </p>
                  <p className="font-jetbrains text-sm text-gray-500 mt-3">
                    That's <span className="text-white font-bold">{formatUsd(math.extraYearly)}</span> in a year.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'roi-calculator', action: 'signup' })}>
              <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                Start free — build your first tiered quote
              </Button>
            </Link>
            <p className="font-jetbrains text-sm text-gray-500 mt-3">Free account — no credit card required.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Where these numbers come from</h2>
          <p className="font-jetbrains text-base text-gray-600 mb-8 leading-relaxed">
            The lift assumptions above are grounded in the <em>ServiceTitan industry close-rate survey</em> (reported in ACHR News) and secondary HVAC sales data. Your mileage varies by trade, market, and how well your tiers are written — but the mechanism is the same across service industries.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 border border-black rounded-xl bg-white">
              <p className="font-instrument text-4xl font-bold mb-2">+10pp</p>
              <p className="font-jetbrains text-sm text-gray-700 leading-relaxed">
                Average close-rate lift when contractors offer <strong>four or more options</strong> on a quote instead of one flat price. Industry-wide survey of service contractors.
              </p>
            </div>
            <div className="p-6 border border-black rounded-xl bg-white">
              <p className="font-instrument text-4xl font-bold mb-2">~+45%</p>
              <p className="font-jetbrains text-sm text-gray-700 leading-relaxed">
                Typical average-ticket lift when customers self-select a "better" or "best" tier. HVAC benchmark: ~$4,500 baseline install climbs to ~$6,500 when tiered with financing + rebate math.
              </p>
            </div>
            <div className="p-6 border border-black rounded-xl bg-white">
              <p className="font-instrument text-4xl font-bold mb-2">42%</p>
              <p className="font-jetbrains text-sm text-gray-700 leading-relaxed">
                Of tickets get financed when the quote leads with a <strong>monthly payment</strong>, vs 21% when leading with the total price. (HVAC sales data — but translates anywhere financing is offered.)
              </p>
            </div>
            <div className="p-6 border border-black rounded-xl bg-white">
              <p className="font-instrument text-4xl font-bold mb-2">20–30%</p>
              <p className="font-jetbrains text-sm text-gray-700 leading-relaxed">
                Typical proposal acceptance rate across service industries. Most contractors sit at the low end; tiered quoting is what moves the needle toward the top of that range.
              </p>
            </div>
          </div>
          <p className="font-jetbrains text-xs text-gray-500 mt-6 leading-relaxed">
            These are industry benchmarks, not a guarantee. The calculator assumes the mechanism works as well for your business as it does for the contractors in the underlying surveys. Results depend on quote writing, pricing, and market.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">See what a tiered quote actually looks like</h2>
          <p className="font-jetbrains text-base text-gray-600 mb-8 leading-relaxed">
            This is a live demo quote — the same kind your customers would see, with interactive Good/Better/Best tiers they can tap through on their phone. No signup needed to view.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/demo-quote/hvac" onClick={() => analytics.track('CTA Clicked', { location: 'roi-calculator', action: 'view-demo' })}>
              <Button className="px-8 py-4 text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg">
                See a live HVAC quote →
              </Button>
            </Link>
            <Link to="/templates" onClick={() => analytics.track('CTA Clicked', { location: 'roi-calculator', action: 'browse-templates' })}>
              <Button className="px-8 py-4 text-lg font-semibold bg-white text-black border border-black hover:bg-gray-50 rounded-lg">
                Browse industry templates
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Ready to add that number to your business?</h2>
          <p className="font-jetbrains text-gray-400 mb-8 max-w-2xl mx-auto">
            Free forever for your first quotes. $29 one-time to remove QuoteTier branding whenever you're ready — no subscription, no credit card up front.
          </p>
          <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'roi-calculator-final', action: 'signup' })}>
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Start free
            </Button>
          </Link>
          <p className="font-jetbrains text-sm text-gray-500 mt-4">Takes under a minute.</p>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default RoiCalculator;
